import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { apiClient } from "../lib/apiClient";
import { useLanguage } from "../context/LanguageContext";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { ContactsSection } from "../components/ContactsSection";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Newspaper } from "lucide-react";

// NewsDetailPage with full HomePage structure
export default function NewsDetailPage() {
  const { slug } = useParams();
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const [newsItem, setNewsItem] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [otherNews, setOtherNews] = React.useState([]);
  const [loadingOtherNews, setLoadingOtherNews] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Fetch the current news article
  React.useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/public/news/by-slug/${slug}`, { params: { lang } });
        const data = response.data;
        setNewsItem(data);
        setError("");
        
        // Update URL to use the correct slug for the current language
        const correctSlug = lang === "en" ? data.slug_en : data.slug_uk;
        if (correctSlug && correctSlug !== slug) {
          // Replace URL without adding to history to avoid back button issues
          navigate(`/news/${correctSlug}?lang=${lang}`, { replace: true });
        }
      } catch (err) {
        console.error("Failed to load news detail", err);
        setError(
          lang === "en"
            ? "This news item is not available. It may be unpublished or removed."
            : "Ця новина недоступна. Можливо, її знято з публікації або видалено."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [slug, lang, navigate]);

  // Update meta tags when news item loads
  React.useEffect(() => {
    if (!newsItem) return;

    // Get SEO data - API returns seo_title and seo_description based on lang
    const seoTitle = newsItem.seo_title || newsItem.title;
    const seoDescription = newsItem.seo_description || newsItem.summary || "";

    // Update document title
    document.title = `${seoTitle} | Strategic Lobbying Alliance`;

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = seoDescription;

    // Update or create Open Graph tags
    const ogTags = [
      { property: 'og:title', content: seoTitle },
      { property: 'og:description', content: seoDescription },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: window.location.href },
    ];

    if (newsItem.image_url) {
      ogTags.push({ property: 'og:image', content: newsItem.image_url });
    }

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });

    // Update or create Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: seoTitle },
      { name: 'twitter:description', content: seoDescription },
    ];

    if (newsItem.image_url) {
      twitterTags.push({ name: 'twitter:image', content: newsItem.image_url });
    }

    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    });

    // Cleanup function to reset title on unmount
    return () => {
      document.title = 'Emergent | Fullstack App';
    };
  }, [newsItem, lang]);

  // Fetch other news articles (excluding current one)
  React.useEffect(() => {
    const fetchOtherNews = async () => {
      try {
        setLoadingOtherNews(true);
        const response = await apiClient.get("/public/news", {
          params: { lang, published_only: true, limit: 6 },
        });
        if (Array.isArray(response.data)) {
          // Filter out the current article by slug
          const filtered = response.data.filter((item) => item.slug !== slug);
          setOtherNews(filtered);
        } else {
          setOtherNews([]);
        }
      } catch (err) {
        console.error("Failed to load other news", err);
        setOtherNews([]);
      } finally {
        setLoadingOtherNews(false);
      }
    };

    fetchOtherNews();
  }, [slug, lang]);

  const handleNavClick = (anchor) => {
    // Navigate to home page with anchor
    navigate("/", { state: { scrollTo: anchor } });
  };

  return (
    <div className="min-h-screen bg-slate-200 text-slate-900 flex flex-col">
      <SiteHeader
        lang={lang}
        onLangChange={setLang}
        onNavClick={handleNavClick}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        showNavigation={true}
      />
      
      <main className="flex-1">
        {/* Article Content Section */}
        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto max-w-4xl px-4 md:px-6">
            {loading && (
              <p className="text-sm text-slate-600">
                {lang === "en" ? "Loading article..." : "Завантаження матеріалу..."}
              </p>
            )}
            {!loading && error && (
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-4">{error}</p>
                <Button
                  onClick={() => navigate("/")}
                  className="rounded-full bg-amber-500 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-900 hover:bg-amber-400"
                >
                  {lang === "en" ? "Back to Homepage" : "Повернутися на головну"}
                </Button>
              </div>
            )}
            {!loading && newsItem && (
              <article className="space-y-6">
                {newsItem.image_url && (
                  <div className="relative h-72 sm:h-96 overflow-hidden rounded-3xl shadow-xl shadow-slate-900/20">
                    <img
                      src={newsItem.image_url}
                      alt={newsItem.title}
                      className="h-full w-full object-cover"
                      loading="eager"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
                  </div>
                )}
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    <span>
                      {newsItem.published_at
                        ? new Date(newsItem.published_at).toLocaleDateString(lang === "en" ? "en-GB" : "uk-UA")
                        : ""}
                    </span>
                    <div className="flex flex-wrap gap-1 text-[10px]">
                      {newsItem.tags?.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700 border border-slate-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                    {newsItem.title}
                  </h1>
                  {newsItem.summary && (
                    <p className="text-base leading-relaxed text-slate-700 border-l-4 border-amber-500 pl-4 py-2 bg-slate-50 rounded-r">
                      {newsItem.summary}
                    </p>
                  )}
                  {newsItem.content && (
                    <div
                      className="prose prose-base max-w-none text-slate-800 prose-headings:text-slate-900 prose-a:text-amber-700 hover:prose-a:text-amber-500 prose-strong:text-slate-900 prose-p:leading-relaxed"
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(newsItem.content, {
                          ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'a', 'br', 'p', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'hr', 'span', 'div'],
                          ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
                        })
                      }}
                    />
                  )}
                  <div className="pt-6 border-t border-slate-200">
                    <Button
                      onClick={() => navigate("/")}
                      className="group inline-flex items-center gap-2 rounded-full bg-transparent px-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700 ring-1 ring-amber-500/70 transition-colors hover:bg-amber-500 hover:text-white"
                    >
                      {lang === "en" ? "← Back to Homepage" : "← Повернутися на головну"}
                    </Button>
                  </div>
                </div>
              </article>
            )}
          </div>
        </section>

        {/* Other News Section */}
        {otherNews.length > 0 && (
          <section className="bg-[#041e41] py-16 sm:py-20">
            <div className="mx-auto max-w-6xl px-4 md:px-6">
              <div className="mb-8">
                <p className="text-base font-semibold uppercase tracking-[0.2em] text-amber-400">
                  {lang === "en" ? "Other news from SLA" : "Інші новини SLA"}
                </p>
              </div>
              {loadingOtherNews && (
                <p className="text-sm text-slate-400">
                  {lang === "en" ? "Loading news..." : "Завантаження новин..."}
                </p>
              )}
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {otherNews.map((item) => (
                  <Card
                    key={item.id}
                    className="group flex h-full flex-col overflow-hidden border-slate-200 bg-slate-50 shadow-lg shadow-slate-900/30 cursor-pointer transition-transform hover:scale-[1.02]"
                    onClick={() => navigate(`/news/${item.slug}`)}
                  >
                    {item.image_url && (
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                          loading="lazy"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/25 via-transparent to-transparent" />
                      </div>
                    )}
                    <CardContent className="flex flex-1 flex-col gap-3 p-5">
                      <div className="flex items-center justify-between gap-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                        <span>
                          {item.published_at
                            ? new Date(item.published_at).toLocaleDateString(lang === "en" ? "en-GB" : "uk-UA")
                            : ""}
                        </span>
                      </div>
                      <h3 className="font-display text-[17px] font-semibold tracking-tight text-slate-900 line-clamp-2">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-slate-700 line-clamp-3">
                        {item.summary && item.summary.length > 120
                          ? `${item.summary.slice(0, 120).trimEnd()}…`
                          : item.summary}
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        className="group mt-auto inline-flex items-center gap-2 rounded-full bg-transparent px-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700 ring-1 ring-amber-500/70 transition-colors hover:bg-amber-500 hover:text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/news/${item.slug}`);
                        }}
                      >
                        <Newspaper className="h-3.5 w-3.5" />
                        <span>{lang === "en" ? "Read more" : "Докладніше"}</span>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Contacts Section */}
        <ContactsSection id="contacts" lang={lang} />
      </main>

      <SiteFooter lang={lang} />
    </div>
  );
}
